import React, {useCallback, useLayoutEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import axios from "axios";
import Utils from "../../../../../../../core/services/utils/utils";
import LoadingLottieAnimation from "../../../../lottie-animations/loading";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Box} from "@mui/material";
import classnames from "classnames";
import TryAgain from "../../../../try-again";

const TechnicalInformationSection = ({partId, cache, updateCache, propertyOrders}) => {
    const [propertyValues, setPropertyValues] = useState([]);
    const [loadingProperties, setLoadingProperties] = useState(!cache.technicalProperties);
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingProperties, [loadingProperties]);

    /**
     * Fetches the technical properties of the part.
     */
    const getTechnicalProperties = useCallback(async (cancelToken) => {
        setLoadingProperties(true);
        const response = await Api.listPartProperties(partId, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoadingProperties(false);
        }
        if (response?.resultFlag) {
            setPropertyValues(response?.data ?? []);
            updateCache('technicalProperties', response?.data ?? []);
        }
    }, [isMounted, partId, updateCache])

    /**
     * Fetches the technical properties of the part.
     */
    useLayoutEffect(() => {
        if (cache.technicalProperties) {
            setPropertyValues(cache.technicalProperties);
            setLoadingProperties(false);
            return;
        }
        const cancelTokenSource = axios.CancelToken.source();
        void getTechnicalProperties(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [getTechnicalProperties, cache.technicalProperties]);

    const properties = useMemo(() => {
        const props = {};
        for (const propertyValue of propertyValues) {
            props[propertyValue.property.id] = {
                ...props[propertyValue.property.id],
                ...propertyValue.property,
                values: [...(props[propertyValue.property.id]?.values ?? []), Utils.formatTechnicalPropertyValueDisplay(propertyValue.property.industryPropertyTypeId, propertyValue.value)].filter(Boolean),
            };
        }
        const res = Object.values(props).filter(e => e.values?.length > 0);
        const ordered = [];
        for (const id of propertyOrders) {
            const found = res.find((item) => item.id === id);
            if (found)
                ordered.push(found);
        }
        const rest = res.filter((item) => !ordered.includes(item));
        rest.sort((a, b) => a.title.localeCompare(b.title));
        ordered.push(...rest);
        return ordered;
    }, [propertyValues, propertyOrders])

    if (loading) {
        return (
            <LoadingLottieAnimation width={'50%'}/>
        );
    }

    return (
        <Grid2 container>
            {
                properties.map((property, index, array) => (
                    <Grid2 key={property.id} xs={12} className={'px-3'}>
                        <Box className='d-flex flex-wrap align-items-stretch justify-content-start h-100'>
                            <Grid2
                                xs={6} className={classnames('px-3 py-3 background-tertiary')}
                                sx={{
                                    borderTopLeftRadius: index === 0 ? 10 : 0,
                                    borderBottomLeftRadius: index === array.length - 1 ? 10 : 0,
                                }}
                            >
                                <p className='text-left font-weight-semi-bold text-sm text-color-1 mr-4'>
                                    {property.title}:
                                </p>
                            </Grid2>
                            <Grid2
                                xs={6}
                                className={'px-3 py-3 background-default'}
                                sx={{
                                    borderTopRightRadius: index === 0 ? 10 : 0,
                                    borderBottomRightRadius: index === array.length - 1 ? 10 : 0,
                                }}>
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center'}}>
                                    {property.values.map((v, i) => (
                                        <p key={i} className='text-left text-sm text-color-1 m-0'>
                                            {v}
                                            {i < property.values.length - 1 && (typeof v === 'string' || typeof v === 'number') && ","}
                                        </p>
                                    ))}
                                </Box>
                            </Grid2>
                        </Box>
                    </Grid2>
                ))
            }
            {
                properties.length === 0 &&
                <TryAgain
                    showButton={false}
                    text={"This product has no technical properties at this moment."}
                />
            }
        </Grid2>
    );
}

export default TechnicalInformationSection;